import { convertDistance } from '@turf/turf';

import { Inject, Injectable } from '@angular/core';

import { FsMapGeometryServiceProvider, IFsMap } from '@lib/geo/core';
import { SnLonLat, SnLonLatBounds, SnLonLatLike } from '@lib/geo/helpers';
import { IMarker } from '@lib/geo/map';

import { DistanceUnit } from '../enums';

const DEFAULT_ZOOM = 17;

@Injectable()
export class MapGeometryFacadeService<TMap extends IFsMap> {
  constructor(
    @Inject(FsMapGeometryServiceProvider) private _provider: FsMapGeometryServiceProvider<TMap>,
  ) {}

  public setMap(map: TMap): void {
    this._provider.setMap(map);
  }

  public getZoom(): number {
    try {
      return this._provider?.getZoom() ?? DEFAULT_ZOOM;
    } catch (error) {
      return DEFAULT_ZOOM;
    }
  }

  public setZoom(zoom: number): void {
    try {
      this._provider?.setZoom(zoom);
    } catch (error) {
      // Silently fail if provider not available or operation fails
    }
  }

  public zoomIn(): void {
    try {
      this._provider?.zoomIn();
    } catch (error) {
      // Silently fail if provider not available or operation fails
    }
  }

  public zoomOut(): void {
    try {
      this._provider?.zoomOut();
    } catch (error) {
      // Silently fail if provider not available or operation fails
    }
  }

  public getCenter(): SnLonLat {
    try {
      return this._provider?.getCenter() ?? new SnLonLat(0, 0);
    } catch (error) {
      throw new Error('Failed to get map center. Map may not be ready.');
    }
  }

  public setCenter(coords: SnLonLatLike): void {
    try {
      this._provider?.setCenter(coords);
    } catch (error) {
      // Silently fail if provider not available or operation fails
    }
  }

  public goToCoordinates(coords: SnLonLatLike, zoom?: number): void {
    try {
      this._provider?.goToCoordinates(coords, zoom);
    } catch (error) {
      // Silently fail if provider not available or operation fails
    }
  }

  public panTo(coords: SnLonLatLike): void {
    try {
      this._provider?.panTo(coords);
    } catch (error) {
      // Silently fail if provider not available or operation fails
    }
  }

  public getBounds(): SnLonLatBounds {
    try {
      const bounds = this._provider?.getBounds();
      return bounds instanceof SnLonLatBounds ? bounds : new SnLonLatBounds([0, 0], [0, 0]);
    } catch (error) {
      throw new Error('Failed to get map bounds. Map may not be ready.');
    }
  }

  public fitBounds(bounds: SnLonLatBounds): void {
    try {
      this._provider?.fitBounds(bounds);
    } catch (error) {
      // Silently fail if provider not available or operation fails
    }
  }

  public calculateBoundsFromMarkers(markers: IMarker<TMap>[]): SnLonLatBounds | null {
    if (markers.length === 0) {
      return null;
    }

    const positions = markers.map((marker) => marker.position);
    let minLat = positions[0].lat;
    let maxLat = positions[0].lat;
    let minLon = positions[0].lon;
    let maxLon = positions[0].lon;

    for (const position of positions) {
      minLat = Math.min(minLat, position.lat);
      maxLat = Math.max(maxLat, position.lat);
      minLon = Math.min(minLon, position.lon);
      maxLon = Math.max(maxLon, position.lon);
    }

    return new SnLonLatBounds([minLon, minLat], [maxLon, maxLat]);
  }

  public convertDistance(distance: number, fromUnit: DistanceUnit, toUnit: DistanceUnit): number {
    return convertDistance(distance, fromUnit, toUnit);
  }
}
